import * as React from "react";

import { productsQuery } from "./app-data-queries";
import { queryClient } from "./query-client";
import { startEarlyErrorBuffer } from "./sentry-early-errors.web";

const SentryInitializer = React.lazy(() => import("./sentry-init.web"));

// Fouten van vóór de Sentry-init bewaren; sentry-init geeft ze door.
startEarlyErrorBuffer();

// Sentry (~150 KB gz) mag niet concurreren met de producten en de eerste
// afbeeldingen: pas laden als de producten er zijn en de browser idle is.
// Komen de producten niet (API stuk), dan na FALLBACK_MS toch.
const FALLBACK_MS = 3000;
const IDLE_TIMEOUT_MS = 2000;

export default function SentryLoader() {
  const [shouldLoad, setShouldLoad] = React.useState(false);

  React.useEffect(() => {
    let started = false;
    let cancelIdle = () => {};
    let fallback: ReturnType<typeof setTimeout> | undefined;
    let unsubscribe = () => {};
    const start = () => {
      if (started) return;
      started = true;
      clearTimeout(fallback);
      unsubscribe();
      cancelIdle = whenIdle(() => setShouldLoad(true));
    };
    fallback = setTimeout(start, FALLBACK_MS);
    unsubscribe = whenProductsLoaded(start);
    return () => {
      clearTimeout(fallback);
      unsubscribe();
      cancelIdle();
    };
  }, []);

  if (!shouldLoad) return null;

  return (
    <React.Suspense fallback={null}>
      <SentryInitializer />
    </React.Suspense>
  );
}

// Roept onLoaded aan zodra de productenquery data heeft (meteen als de
// catalogus uit de lokale cache kwam). Geeft een afmeldfunctie terug.
function whenProductsLoaded(onLoaded: () => void): () => void {
  const cache = queryClient.getQueryCache();
  const hasProducts = () => cache.find({ queryKey: productsQuery.queryKey })?.state.data !== undefined;
  if (hasProducts()) {
    // na de huidige render, zodat de producten eerst op het scherm staan
    const timer = setTimeout(onLoaded, 0);
    return () => clearTimeout(timer);
  }
  const unsubscribe = cache.subscribe(() => {
    if (hasProducts()) {
      unsubscribe();
      onLoaded();
    }
  });
  return unsubscribe;
}

// requestIdleCallback met terugval voor browsers zonder (oudere Safari).
function whenIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(handle);
  }
  const timer = setTimeout(callback, 200);
  return () => clearTimeout(timer);
}
